/**
 * inflight-writes.ts — 在飞写请求登记表（#356 目标 4「页面 pending 锁定提供可恢复出口」）。
 *
 * 传输层（PR #434）保证每个请求 30s 内必 settle 并抛出带 `serverStateUnknown` 的 typed error，
 * 页面因此不会**永远**挂住。但这 30 秒里运营没有任何出口：按钮 disabled、没有「还在处理」的
 * 提示、也无法中断。本模块补的就是这段 —— 登记每个在飞的**写**请求及其 AbortController，
 * 由 AppShell 级的 `StuckWriteNotice` 在超过阈值时给出「取消等待」。
 *
 * **为什么做成全局而不是逐页**：原计划（`docs/plans/2026-08-16-admin-api-request-timeout.md`）把
 * 页面级出口判成「逐页的交互决策」，理由是各表单在中止后该保留草稿还是回滚乐观更新各不相同。
 * 核对当前代码后这条不成立：全仓 **0 处 `onMutate`**（没有任何乐观更新可回滚），且**没有任何
 * `onError` 分支清空表单**（草稿本来就都保住了）。逐页做的话要动 105 个 mutation 站点、
 * 40 余个文件，收益却与在一处登记完全相同。
 *
 * 只登记**非 GET**：读请求卡住时刷新页面即可，没有「服务端可能已落库」的顾虑，给取消按钮
 * 反而是噪音。这与 `AdminRequestAbortedError.serverStateUnknown` 的判据同源。
 *
 * 取消后不需要任何页面配合：`api/client.ts` 会把中止分流成 `AdminRequestCanceledError`，
 * 各页既有的 `onError` 兜底分支照常拿到它，展示 `REQUEST_CANCELED` 的中文文案
 * （「请求已取消；若是写操作，服务端可能已生效，请刷新页面确认」）。
 */

/** 一条在飞的写请求。`abort` 就是 `api/client.ts` 里那个 controller 的 abort。 */
export type InflightWrite = {
  id: string;
  method: string;
  /** 仅路径部分，不含 query —— query 可能带筛选值，进 UI 只是噪音 */
  path: string;
  /** `performance.now()` 起点，算已等待时长用 */
  startedAt: number;
  abort: () => void;
};

let entries: InflightWrite[] = [];
const listeners = new Set<() => void>();
let seq = 0;

/**
 * 快照必须是**稳定引用**：`useSyncExternalStore` 每次渲染都调 getSnapshot，返回新数组会被判成
 * 变化，导致无限重渲。故只在 register / unregister 时换新数组，其余时刻原样返回同一个。
 */
function emit(): void {
  for (const l of listeners) l();
}

export function listInflightWrites(): InflightWrite[] {
  return entries;
}

export function subscribeInflightWrites(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 登记一条在飞写请求，返回注销函数。
 *
 * 调用方必须在 `finally` 里注销 —— 漏注销会让通知条永久显示一条已经结束的请求，
 * 而它的 abort 已经无效，运营点了没反应。
 */
export function registerInflightWrite(input: Omit<InflightWrite, 'id'>): () => void {
  seq += 1;
  const id = `w${seq}`;
  entries = [...entries, { ...input, id }];
  emit();
  let done = false;
  return () => {
    // 幂等：`finally` 与组件卸载都可能调到，重复执行不该误删后来的同 id 项（id 单调递增故不会撞，
    // 但保持幂等能让调用点无需自己记状态）
    if (done) return;
    done = true;
    entries = entries.filter((e) => e.id !== id);
    emit();
  };
}

/** 供测试重置模块态 —— 模块级单例跨用例会串味。 */
export function __resetInflightWritesForTest(): void {
  entries = [];
  listeners.clear();
  seq = 0;
}
